/**
 * ThreiaTotemDisplay: HUD widget showing whether the player holds Threia's Totem.
 *
 * Displays a small totem icon at a fixed HUD position. The icon is hidden when
 * the totem is not held and shown with a dramatic glow effect when collected.
 * When the totem is consumed by a blessing, it plays a dissolve animation.
 *
 * Positioned at the bottom-left corner of the playable area as a persistent
 * divine indicator.
 */

import Phaser from 'phaser';
import { TEX_THREIA_TOTEM, TEX_GLOW } from '../utils/constants.js';
import {
  THREIA_TOTEM_CONSUME_DURATION,
} from '../config/game-config.js';

export default class ThreiaTotemDisplay {
  /**
   * @param {Phaser.Scene} scene - The HUD scene.
   * @param {number} x - Center X position.
   * @param {number} y - Center Y position.
   */
  constructor(scene, x, y) {
    this.scene = scene;
    this.x = x;
    this.y = y;

    /** @type {boolean} Whether the player currently holds the totem. */
    this._hasTotem = false;

    /** @type {Phaser.GameObjects.Sprite|null} Totem icon sprite. */
    this._icon = null;

    /** @type {Phaser.GameObjects.Text|null} "TOTEM" label. */
    this._label = null;

    /** @type {Phaser.GameObjects.Graphics|null} Glow halo behind icon. */
    this._glowHalo = null;

    /** @type {Phaser.Tweens.Tween|null} Pulsing glow tween. */
    this._glowTween = null;

    /** @type {Phaser.Tweens.Tween|null} Float tween for the icon. */
    this._floatTween = null;

    // Create the display elements (hidden initially)
    this._createDisplay();
  }

  /**
   * Show the totem display with a dramatic collect animation.
   * Called when the player picks up a Threia Totem.
   */
  showTotem() {
    if (this._hasTotem) return;
    this._hasTotem = true;

    if (!this._icon || !this.scene) return;

    // Make visible
    this._icon.setVisible(true).setAlpha(0).setScale(0);
    this._label.setVisible(true).setAlpha(0);

    // Pop-in animation
    this.scene.tweens.add({
      targets: this._icon,
      scale: 1.6,
      alpha: 1,
      duration: 350,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!this._icon || !this._icon.scene) return;
        this.scene.tweens.add({
          targets: this._icon,
          scale: 1.2,
          duration: 200,
          ease: 'Sine.easeInOut',
        });
      },
    });

    // Label fade-in
    this.scene.tweens.add({
      targets: this._label,
      alpha: 1,
      duration: 400,
      delay: 200,
    });

    // Start glow effect
    this._startGlow();

    // Start gentle float
    this._startFloat();

    // Particle burst on collect
    this._playCollectBurst();
  }

  /**
   * Hide the totem display with a consume/dissolve animation.
   * Called when the blessing consumes the totem.
   *
   * @param {Function} [onComplete] - Optional callback when animation finishes.
   */
  consumeTotem(onComplete) {
    if (!this._hasTotem) {
      if (onComplete) onComplete();
      return;
    }

    this._hasTotem = false;

    if (!this._icon || !this.scene) {
      if (onComplete) onComplete();
      return;
    }

    // Stop glow
    this._stopGlow();
    this._stopFloat();

    // Flash bright white before fading
    if (this._icon.active) {
      this._icon.setTint(0xffffff);
    }

    // Particle burst — divine consumption
    try {
      const burst = this.scene.add.particles(this._icon.x, this._icon.y, TEX_GLOW, {
        lifespan: { min: 300, max: 600 },
        speed: { min: 15, max: 50 },
        angle: { min: 0, max: 360 },
        scale: { start: 0.3, end: 0 },
        alpha: { start: 0.8, end: 0 },
        frequency: -1,
        quantity: 10,
      });
      burst.setDepth(220);
      burst.explode(10);

      this.scene.time.delayedCall(700, () => {
        if (burst) burst.destroy();
      });
    } catch (_) { /* fallback */ }

    // Shrink + fade + ascend
    this.scene.tweens.add({
      targets: this._icon,
      scale: 0,
      alpha: 0,
      y: this._icon.y - 15,
      duration: THREIA_TOTEM_CONSUME_DURATION,
      ease: 'Back.easeIn',
      onComplete: () => {
        if (this._icon) {
          this._icon.setVisible(false);
          this._icon.clearTint();
          this._icon.y = this.y;
        }
        if (onComplete) onComplete();
      },
    });

    // Label fade out
    this.scene.tweens.add({
      targets: this._label,
      alpha: 0,
      duration: THREIA_TOTEM_CONSUME_DURATION * 0.7,
      onComplete: () => {
        if (this._label) this._label.setVisible(false);
      },
    });
  }

  /**
   * Set the totem state directly (for save restore).
   * No animation, just show/hide.
   *
   * @param {boolean} hasTotem
   */
   setState(hasTotem) {
    this._hasTotem = hasTotem;

    if (!this._icon) return;

    if (hasTotem) {
      this._icon.setVisible(true).setAlpha(1).setScale(1.2);
      this._label.setVisible(true).setAlpha(1);
      this._startGlow();
      this._startFloat();
    } else {
      this._icon.setVisible(false);
      this._label.setVisible(false);
      this._stopGlow();
      this._stopFloat();
    }
  }

  /**
   * React to prayer phase changes — intensify glow when the player is praying.
   * Phase 0 = no prayer, 1-3 = increasing glow intensity.
   *
   * @param {number} phase - Current prayer glow phase (0-3)
   */
  onPrayerPhase(phase) {
    if (!this._hasTotem || !this._icon || !this.scene) return;

    // Pulse the icon scale briefly on each phase change
    if (phase > 0) {
      this.scene.tweens.add({
        targets: this._icon,
        scaleX: 1.5,
        scaleY: 1.5,
        duration: 120,
        yoyo: true,
        ease: 'Sine.easeOut',
      });
    }
  }

  /**
   * Play a bright flash on the totem icon when it repels a bat.
   * Creates a quick white burst around the icon that fades out.
   */
  playRepelFlash() {
    if (!this._hasTotem || !this._icon || !this.scene) return;

    // Strong scale punch on the icon
    this.scene.tweens.add({
      targets: this._icon,
      scaleX: 2.0,
      scaleY: 2.0,
      duration: 100,
      yoyo: true,
      ease: 'Sine.easeOut',
    });

    // Bright circular flash behind the icon
    const flashGfx = this.scene.add.graphics();
    flashGfx.setDepth(208);
    const cx = this._icon.x;
    const cy = this._icon.y;
    const alphaObj = { val: 0.9 };

    const drawFlash = () => {
      flashGfx.clear();
      flashGfx.fillStyle(0xffffff, alphaObj.val * 0.4);
      flashGfx.fillCircle(cx, cy, 22);
      flashGfx.fillStyle(0xffffff, alphaObj.val);
      flashGfx.fillCircle(cx, cy, 12);
    };

    drawFlash();

    this.scene.tweens.add({
      targets: alphaObj,
      val: 0,
      duration: 500,
      ease: 'Quad.easeOut',
      onUpdate: drawFlash,
      onComplete: () => {
        flashGfx.destroy();
      },
    });

    // Particle burst
    this._playCollectBurst();
  }

  /**
   * Destroy all display elements.
   */
  destroy() {
    this._stopGlow();
    this._stopFloat();

    if (this._icon) {
      this._icon.destroy();
      this._icon = null;
    }
    if (this._label) {
      this._label.destroy();
      this._label = null;
    }
  }

  // ---------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------

  /**
   * Create the icon and label elements (hidden initially).
   * @private
   */
  _createDisplay() {
    // Totem icon sprite
    this._icon = this.scene.add.sprite(this.x, this.y, TEX_THREIA_TOTEM)
      .setOrigin(0.5)
      .setDepth(210)
      .setScale(1.2)
      .setVisible(false);

    // Small label under the icon
    this._label = this.scene.add.text(this.x, this.y + 14, 'TOTEM', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '7px',
      color: '#888888',
    })
      .setOrigin(0.5, 0)
      .setDepth(210)
      .setVisible(false);
  }

  /**
   * Start the pulsing glow halo behind the icon.
   * @private
   */
  _startGlow() {
    if (this._glowHalo || !this.scene) return;

    this._glowHalo = this.scene.add.graphics();
    this._glowHalo.setDepth(209);

    const alphaObj = { val: 0.25 };
    this._drawGlowHalo(alphaObj.val);

    this._glowTween = this.scene.tweens.add({
      targets: alphaObj,
      val: 0.08,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        this._drawGlowHalo(alphaObj.val);
      },
    });
  }

  /**
   * Stop the glow effect.
   * @private
   */
  _stopGlow() {
    if (this._glowTween) {
      this._glowTween.stop();
      this._glowTween = null;
    }
    if (this._glowHalo) {
      this._glowHalo.destroy();
      this._glowHalo = null;
    }
  }

  /**
   * Start a gentle float animation on the icon.
   * @private
   */
  _startFloat() {
    if (this._floatTween || !this._icon || !this.scene) return;

    this._floatTween = this.scene.tweens.add({
      targets: this._icon,
      y: this.y - 2,
      duration: 1600,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
  }

  /**
   * Stop the float animation.
   * @private
   */
  _stopFloat() {
    if (this._floatTween) {
      this._floatTween.stop();
      this._floatTween = null;
    }
    if (this._icon) {
      this._icon.y = this.y;
    }
  }

  /**
   * Draw the glow halo behind the icon.
   * @private
   * @param {number} alpha
   */
  _drawGlowHalo(alpha) {
    if (!this._glowHalo || !this._icon) return;
    this._glowHalo.clear();

    // Outer soft glow
    this._glowHalo.fillStyle(0xffffff, alpha * 0.4);
    this._glowHalo.fillCircle(this._icon.x, this._icon.y, 16);

    // Inner brighter core
    this._glowHalo.fillStyle(0xffffff, alpha);
    this._glowHalo.fillCircle(this._icon.x, this._icon.y, 10);
  }

  /**
   * Play a particle burst effect when collecting the totem.
   * @private
   */
  _playCollectBurst() {
    if (!this.scene || !this._icon) return;

    try {
      const emitter = this.scene.add.particles(this._icon.x, this._icon.y, TEX_GLOW, {
        lifespan: { min: 300, max: 600 },
        speed: { min: 10, max: 35 },
        scale: { start: 0.3, end: 0 },
        alpha: { start: 0.9, end: 0 },
        frequency: -1,
        quantity: 8,
      });
      emitter.setDepth(220);
      emitter.explode(8);

      this.scene.time.delayedCall(700, () => {
        if (emitter) emitter.destroy();
      });
    } catch (_) { /* fallback */ }
  }
}
